import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Users, Rocket, ArrowRight, Copy, Ban, RotateCcw, KeyRound,
  Radio, Clock, Link2, Eye, ImagePlus, Trash2, MessageSquare, Mic, Type,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDur = (s) => {
  s = Math.round(s || 0);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s % 60}s`;
};

function ProjectPicker() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/projects").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <div>
      <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-teal-400/70">Beta Listener Preview</div>
      <h1 className="font-serif text-4xl tracking-tight text-slate-100">Beta Preview</h1>
      <p className="mt-2 text-sm text-slate-400">Publish finished chapters to a small group of trusted beta listeners. Choose a production.</p>
      {rows === null ? <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-teal-400" /></div>
        : (<div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          {(rows || []).map((p) => (
            <button key={p.id} data-testid={`beta-project-${p.id}`} onClick={() => navigate(`/studio/projects/${p.id}/beta`)}
              className="group flex items-center justify-between rounded-xl border border-border bg-[#12161f] p-5 text-left transition-all hover:border-teal-500/40">
              <h3 className="font-serif text-xl text-slate-100">{p.title}</h3>
              <ArrowRight className="h-4 w-4 text-slate-600 transition-transform group-hover:translate-x-1 group-hover:text-teal-400" />
            </button>
          ))}
        </div>)}
    </div>
  );
}

export default function BetaPreview() {
  const { projectId } = useParams();
  if (!projectId) return <ProjectPicker />;
  return <BetaAdmin projectId={projectId} />;
}

function BetaAdmin({ projectId }) {
  const [pub, setPub] = useState(null);
  const [releases, setReleases] = useState([]);
  const [listeners, setListeners] = useState([]);
  const [sel, setSel] = useState({});
  const [relName, setRelName] = useState("");
  const [relNotes, setRelNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [nl, setNl] = useState({ display_name: "", email: "", author_note: "" });
  const [invite, setInvite] = useState(null); // {name, url}
  const [feedback, setFeedback] = useState([]);

  const load = useCallback(async () => {
    try {
      const [p, r, l, fb] = await Promise.all([
        api.get(`/projects/${projectId}/beta/publishable`),
        api.get(`/projects/${projectId}/beta/releases`),
        api.get(`/projects/${projectId}/beta/listeners`),
        api.get(`/projects/${projectId}/beta/feedback`),
      ]);
      setPub(p.data.chapters); setReleases(r.data.releases); setListeners(l.data.listeners);
      setFeedback(fb.data.feedback || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(sel).filter((k) => sel[k]);

  const publish = async () => {
    if (!selectedIds.length) { toast.error("Select at least one chapter."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/beta/releases`, {
        name: relName, notes: relNotes, chapter_ids: selectedIds,
      });
      toast.success(`Published ${data.name} · ${data.chapters.length} chapters.`);
      setSel({}); setRelName(""); setRelNotes(""); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const inviteUrl = (path) => `${window.location.origin}${path}`;

  const deleteRelease = async (r, isLive) => {
    const warn = isLive
      ? `Delete "${r.name}"? This is the release your listeners currently see — they will fall back to the previous release (or an empty library). Listener progress for this release is removed. This cannot be undone.`
      : `Delete "${r.name}"? Listener progress for this release is removed. This cannot be undone.`;
    if (!window.confirm(warn)) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/beta/releases/${r.id}`);
      toast.success(`Deleted ${r.name}.`);
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addListener = async () => {
    if (!nl.display_name.trim()) { toast.error("Listener name is required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/beta/listeners`, nl);
      setInvite({ name: data.display_name, url: inviteUrl(data.invite_path) });
      setNl({ display_name: "", email: "", author_note: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = async (url) => { try { await navigator.clipboard.writeText(url); toast.success("Invite link copied."); } catch { toast.error("Copy failed — select and copy manually."); } };
  const copyInvite = async (id, name) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/beta/listeners/${id}/invite`);
      const url = inviteUrl(data.invite_path);
      setInvite({ name, url });
      try { await navigator.clipboard.writeText(url); toast.success("Invite link copied."); }
      catch { toast.message("Invite link ready below — select and copy it."); }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const revoke = async (id) => { await api.post(`/projects/${projectId}/beta/listeners/${id}/revoke`); toast.success("Access revoked."); await load(); };
  const restore = async (id) => { await api.post(`/projects/${projectId}/beta/listeners/${id}/restore`); toast.success("Access restored."); await load(); };
  const regenerate = async (id, name) => {
    if (!window.confirm("Regenerate the invite link? The old link stops working immediately.")) return;
    const { data } = await api.post(`/projects/${projectId}/beta/listeners/${id}/regenerate`);
    setInvite({ name, url: inviteUrl(data.invite_path) }); toast.success("New invite link generated."); await load();
  };

  const [pvRel, setPvRel] = useState("");
  const [pvListener, setPvListener] = useState("");
  const [coverVersion, setCoverVersion] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);

  const loadCover = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${projectId}`); setCoverVersion(data.cover_version || null); }
    catch { /* ignore */ }
  }, [projectId]);
  useEffect(() => { loadCover(); }, [loadCover]);

  const uploadCover = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes((file.type || "").toLowerCase())) {
      toast.error("Cover must be a JPG, PNG, or WebP image."); return;
    }
    if (file.size > 10 * 1024 * 1024) { toast.error("Cover image must be 10MB or smaller."); return; }
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${projectId}/cover`, fd);
      setCoverVersion(data.cover_version);
      toast.success("Cover art updated. Beta listeners will see it on their next sync.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setCoverBusy(false); }
  };

  const removeCover = async () => {
    if (!window.confirm("Remove the cover art?")) return;
    setCoverBusy(true);
    try { await api.delete(`/projects/${projectId}/cover`); setCoverVersion(null); toast.success("Cover art removed."); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setCoverBusy(false); }
  };

  const previewAsListener = async () => {
    const releaseId = pvRel || (releases[0] && releases[0].id);
    if (!releaseId) { toast.error("Publish a Beta Release first."); return; }
    try {
      const { data } = await api.post(`/projects/${projectId}/beta/preview`, {
        release_id: releaseId, listener_id: pvListener || null,
      });
      window.open(inviteUrl(data.preview_path), "_blank", "noopener");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8" data-testid="beta-admin">
      <div>
        <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-teal-400/70">Beta Listener Preview</div>
        <h1 className="font-serif text-4xl tracking-tight text-slate-100">Beta Preview</h1>
        <p className="mt-2 text-sm text-slate-400">Snapshot finished chapters into a Beta Release and share private links. Your production work never changes what beta listeners are hearing.</p>
      </div>

      {/* Cover art */}
      <section className="rounded-xl border border-border bg-[#0b0e14] p-5" data-testid="beta-cover">
        <div className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-teal-300/80"><ImagePlus className="h-3.5 w-3.5" /> Cover art</div>
        <div className="flex items-center gap-5">
          <div className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-xl border border-border bg-[#12161f]">
            {coverVersion ? (
              <img data-testid="beta-cover-image" src={`${BACKEND}/api/projects/${projectId}/cover?v=${coverVersion}`} alt="Cover art" className="h-full w-full object-cover" />
            ) : (
              <ImagePlus className="h-7 w-7 text-slate-600" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm text-slate-300">{coverVersion ? "This cover shows across the beta listener app and phone lock screens." : "Add a square cover image for your beta listeners. It appears on the welcome screen, library, player, and phone lock screen."}</p>
            <p className="mt-1 text-[11px] text-slate-500">Square works best · JPG, PNG, or WebP · up to 10MB.</p>
            <div className="mt-3 flex items-center gap-2">
              <label data-testid="beta-cover-upload" className={`inline-flex cursor-pointer items-center gap-2 rounded-full bg-teal-500 px-4 py-2 text-xs font-semibold text-slate-950 hover:bg-teal-400 ${coverBusy ? "pointer-events-none opacity-50" : ""}`}>
                {coverBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} {coverVersion ? "Replace cover" : "Upload cover"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="beta-cover-input"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadCover(f); }} />
              </label>
              {coverVersion && (
                <button data-testid="beta-cover-remove" onClick={removeCover} disabled={coverBusy} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-xs text-slate-300 hover:border-red-400/40 hover:text-red-300 disabled:opacity-50">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Publish to Beta */}
      <section className="rounded-xl border border-border bg-[#0b0e14] p-5" data-testid="beta-publish">
        <div className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-teal-300/80"><Rocket className="h-3.5 w-3.5" /> Publish to Beta</div>
        {pub === null ? <Loader2 className="h-5 w-5 animate-spin text-teal-400" />
          : pub.length === 0 ? <p className="text-sm text-slate-400">No chapters yet. Render a chapter (Final or Draft) in Master Assembly first.</p>
          : (<>
            <p className="mb-2 text-xs text-slate-500">Your book in reading order. Select the sections to include in this Beta Release.</p>
            <div className="space-y-1.5">
              {(pub || []).map((c) => (
                <label key={c.chapter_id} data-testid={`beta-pub-${c.chapter_id}`}
                  className={`flex items-center justify-between rounded-lg border border-border bg-[#12161f] px-3 py-2 ${c.ready ? "cursor-pointer hover:border-teal-500/30" : "opacity-55"}`}>
                  <span className="flex items-center gap-2.5">
                    <input type="checkbox" disabled={!c.ready} checked={!!sel[c.chapter_id]} onChange={() => c.ready && toggle(c.chapter_id)}
                      data-testid={`beta-pub-check-${c.chapter_id}`} className="h-4 w-4 accent-teal-500 disabled:opacity-40" />
                    <span className="text-sm text-slate-100">{c.title}</span>
                    {c.ready
                      ? <span className={`rounded px-1.5 py-0.5 text-[9px] uppercase ${c.export_mode === "final" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{c.export_mode}</span>
                      : <span className="rounded bg-slate-600/20 px-1.5 py-0.5 text-[9px] uppercase text-slate-400">audio not ready</span>}
                  </span>
                  <span className="font-mono text-[10px] text-slate-500">{c.ready ? fmtDur(c.duration) : "—"}</span>
                </label>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input data-testid="beta-release-name" value={relName} onChange={(e) => setRelName(e.target.value)} placeholder="Release name (e.g. Beta 0.4)" className="bg-[#0d1016] text-sm" />
              <Input data-testid="beta-release-notes" value={relNotes} onChange={(e) => setRelNotes(e.target.value)} placeholder="Release notes (optional)" className="bg-[#0d1016] text-sm" />
            </div>
            <Button data-testid="beta-publish-btn" onClick={publish} disabled={busy || !selectedIds.length}
              className="mt-3 gap-2 bg-teal-500 text-slate-950 hover:bg-teal-400 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Publish {selectedIds.length || ""} to Beta
            </Button>
          </>)}
      </section>

      {/* Releases */}
      <section data-testid="beta-releases">
        <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-teal-300/80"><Radio data-testid="beta-releases-icon" className="h-3.5 w-3.5" /> Beta releases ({releases.length})</div>
        {releases.length === 0 ? <p className="text-sm text-slate-500">No releases yet.</p>
          : (<div className="space-y-2">{releases.map((r, i) => (
            <div key={r.id} data-testid={`beta-release-${r.id}`} className="rounded-lg border border-border bg-[#12161f] p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-sm text-slate-100">
                  <span className="font-mono text-slate-500">#{r.release_number}</span> · {r.name}
                  {i === 0 && <span data-testid={`beta-release-live-${r.id}`} className="ml-2 rounded-full bg-teal-500/15 px-2 py-0.5 align-middle font-mono text-[9px] uppercase tracking-widest text-teal-300">Live</span>}
                </span>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="font-mono text-[10px] text-slate-500">{new Date(r.published_at).toLocaleDateString()} · {r.chapters.length} ch</span>
                  <button data-testid={`beta-release-delete-${r.id}`} onClick={() => deleteRelease(r, i === 0)} disabled={busy}
                    aria-label={`Delete ${r.name}`} title="Delete release"
                    className="rounded-md p-1 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-300 disabled:opacity-50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {r.notes && <p className="mt-1 text-[11px] text-slate-500">{r.notes}</p>}
              <div className="mt-1.5 flex flex-wrap gap-1.5">{(r.chapters || []).map((c) => (
                <span key={c.chapter_id} className="rounded bg-teal-500/10 px-1.5 py-0.5 text-[10px] text-teal-300">{c.title} · {fmtDur(c.duration)}</span>
              ))}</div>
            </div>
          ))}</div>)}
      </section>

      {/* Listener Feedback */}
      <section data-testid="beta-feedback">
        <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-teal-300/80"><MessageSquare className="h-3.5 w-3.5" /> Listener feedback ({feedback.length})</div>
        {feedback.length === 0 ? <p className="text-sm text-slate-500">No feedback yet. Listeners can leave timestamped notes and voice notes while they listen.</p>
          : (<div className="space-y-2">{feedback.map((f) => (
            <div key={f.feedback_id} data-testid={`beta-feedback-${f.feedback_id}`} className="rounded-lg border border-border bg-[#12161f] p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-xs text-slate-300">
                  {f.kind === "voice" ? <Mic className="h-3.5 w-3.5 text-teal-300" /> : <Type className="h-3.5 w-3.5 text-teal-300" />}
                  <strong className="text-slate-100">{f.listener_name || "Listener"}</strong>
                  <span className="text-slate-500">·</span>
                  <span className="text-slate-400">{f.item_title}</span>
                  <span className="font-mono text-[10px] text-teal-300/70">@ {fmtDur(f.position)}</span>
                </span>
                {f.transcript_status && f.transcript_status !== "n/a" && (
                  <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wider ${f.transcript_status === "ready" ? "bg-teal-500/15 text-teal-300" : f.transcript_status === "failed" ? "bg-slate-600/20 text-slate-400" : "bg-amber-500/15 text-amber-300"}`}>
                    {f.transcript_status === "ready" ? "transcribed" : f.transcript_status}
                  </span>
                )}
              </div>
              {f.passage_label && <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">{f.passage_label}</div>}
              {f.excerpt && <p className="mt-1 border-l-2 border-teal-500/30 pl-2 text-[11px] italic text-slate-500">“{f.excerpt.slice(0, 160)}{f.excerpt.length > 160 ? "…" : ""}”</p>}
              {f.text && <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-100">{f.text}</p>}
              {f.has_audio && (
                <audio data-testid={`beta-feedback-audio-${f.feedback_id}`} controls preload="none"
                  src={`${BACKEND}/api/projects/${projectId}/beta/feedback/${f.feedback_id}/audio`} className="mt-2 h-9 w-full max-w-sm" />
              )}
            </div>
          ))}</div>)}
      </section>

      {/* Preview as Listener (author QA) */}
      {releases.length > 0 && (
        <section className="rounded-xl border border-amber-500/20 bg-amber-500/[0.03] p-5" data-testid="beta-preview-panel">
          <div className="mb-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-amber-300/80"><Eye className="h-3.5 w-3.5" /> Preview as Listener</div>
          <p className="mb-3 text-xs text-slate-400">Experience exactly what a listener sees before you invite anyone. Preview activity never touches real listener progress or analytics.</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Select data-testid="beta-preview-release-select" value={pvRel || (releases[0] && releases[0].id) || ""} onValueChange={setPvRel}>
              <SelectTrigger data-testid="beta-preview-release" className="border-border bg-[#0d1016] text-sm text-slate-200">
                <SelectValue placeholder="Choose a release" />
              </SelectTrigger>
              <SelectContent>
                {(releases || []).map((r) => <SelectItem key={r.id} data-testid={`beta-preview-rel-opt-${r.id}`} value={r.id}>#{r.release_number} · {r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select data-testid="beta-preview-listener-select" value={pvListener || "__generic__"} onValueChange={(v) => setPvListener(v === "__generic__" ? "" : v)}>
              <SelectTrigger data-testid="beta-preview-listener" className="border-border bg-[#0d1016] text-sm text-slate-200">
                <SelectValue placeholder="Generic preview" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="beta-preview-opt-generic" value="__generic__">Generic preview</SelectItem>
                {(listeners || []).map((l) => <SelectItem key={l.id} data-testid={`beta-preview-opt-${l.id}`} value={l.id}>As {l.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="beta-preview-btn" onClick={previewAsListener} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"><Eye className="h-4 w-4" /> Open preview</Button>
          </div>
        </section>
      )}

      {/* Listeners */}
      <section data-testid="beta-listeners">
        <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-teal-300/80"><Users className="h-3.5 w-3.5" /> Beta listeners ({listeners.length})</div>
        <div className="mb-3 grid grid-cols-1 gap-2 rounded-lg border border-border bg-[#0b0e14] p-3 sm:grid-cols-4">
          <Input data-testid="beta-listener-name" value={nl.display_name} onChange={(e) => setNl({ ...nl, display_name: e.target.value })} placeholder="Listener name*" className="bg-[#0d1016] text-sm" />
          <Input data-testid="beta-listener-email" value={nl.email} onChange={(e) => setNl({ ...nl, email: e.target.value })} placeholder="Email (optional)" className="bg-[#0d1016] text-sm" />
          <Input data-testid="beta-listener-note" value={nl.author_note} onChange={(e) => setNl({ ...nl, author_note: e.target.value })} placeholder="Private note (optional)" className="bg-[#0d1016] text-sm" />
          <Button data-testid="beta-add-listener-btn" onClick={addListener} disabled={busy} className="gap-2 bg-teal-500 text-slate-950 hover:bg-teal-400"><Users className="h-4 w-4" /> Add & invite</Button>
        </div>

        {invite && (
          <div data-testid="beta-invite-panel" className="mb-3 rounded-lg border border-teal-500/40 bg-teal-500/[0.06] p-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs text-teal-200"><Link2 className="h-3.5 w-3.5" /> Invite link for <strong>{invite.name}</strong> — you can copy it anytime from the list below.</div>
            <div className="flex items-center gap-2">
              <Input readOnly value={invite.url} data-testid="beta-invite-url" className="bg-[#0d1016] font-mono text-[11px] text-slate-300" onFocus={(e) => e.target.select()} />
              <Button data-testid="beta-copy-invite" onClick={() => copy(invite.url)} className="gap-1 bg-teal-500 text-slate-950 hover:bg-teal-400"><Copy className="h-3.5 w-3.5" /> Copy</Button>
              <button data-testid="beta-invite-dismiss" onClick={() => setInvite(null)} className="text-slate-500 hover:text-slate-300">✕</button>
            </div>
          </div>
        )}

        {listeners.length === 0 ? <p className="text-sm text-slate-500">No listeners invited yet.</p>
          : (<div className="space-y-2">{listeners.map((l) => (
            <div key={l.id} data-testid={`beta-listener-${l.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-[#12161f] p-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-100">{l.display_name}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[9px] uppercase ${l.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>{l.status}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 font-mono text-[10px] text-slate-500">
                  {l.email && <span>{l.email}</span>}
                  <span><Clock className="mr-0.5 inline h-3 w-3" />{l.last_active_at ? `active ${new Date(l.last_active_at).toLocaleDateString()}` : "not opened yet"}</span>
                </div>
                {l.author_note && <div className="mt-0.5 text-[10px] italic text-slate-600">{l.author_note}</div>}
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" data-testid={`beta-copy-link-${l.id}`} onClick={() => copyInvite(l.id, l.display_name)} className="h-7 gap-1 border-border text-xs text-slate-300 hover:border-teal-500/40 hover:text-teal-200"><Copy className="h-3 w-3" /> Copy link</Button>
                <Button size="sm" variant="outline" data-testid={`beta-regenerate-${l.id}`} onClick={() => regenerate(l.id, l.display_name)} className="h-7 gap-1 border-border text-xs text-slate-300"><KeyRound className="h-3 w-3" /> New link</Button>
                {l.status === "active"
                  ? <Button size="sm" variant="outline" data-testid={`beta-revoke-${l.id}`} onClick={() => revoke(l.id)} className="h-7 gap-1 border-red-500/30 text-xs text-red-300 hover:bg-red-500/10"><Ban className="h-3 w-3" /> Revoke</Button>
                  : <Button size="sm" variant="outline" data-testid={`beta-restore-${l.id}`} onClick={() => restore(l.id)} className="h-7 gap-1 border-emerald-500/30 text-xs text-emerald-300 hover:bg-emerald-500/10"><RotateCcw className="h-3 w-3" /> Restore</Button>}
              </div>
            </div>
          ))}</div>)}
      </section>
    </div>
  );
}
